import { CrewStatus, Role, UserStatus } from "../enums";
import {
  CrewAvailability,
  CrewMember,
  CrewUserPayload,
  DropdownItem,
  LeadUser,
  User,
  UserInfo,
} from "../types";
import {
  CREW_DAYS,
  CREW_DEFAULT_DAY_HOURS,
  CREW_DEFAULT_SKILL_LEVEL,
  CREW_EMPTY_AVAILABILITY,
  CREW_OVERTIME_MULTIPLIER,
} from "../constants";

export class UserModel {
  public static buildLeadUserDropdown(leadUsers: LeadUser[]): DropdownItem[] {
    if (!leadUsers) {
      return [];
    }

    return leadUsers.map((user) => ({
      key: user.leadName,
      value: user.leadId,
    }));
  }

  public static buildUserStatusDropdown(): DropdownItem[] {
    return Object.entries(UserStatus).map(([key, value]) => ({
      key: key,
      value: value,
    }));
  }

  public static buildUserRoleDropdown(): DropdownItem[] {
    return Object.entries(Role)
      .filter(([, value]) => value !== Role.Washer && value !== Role.Driver)
      .map(([key, value]) => ({
        key: key.replace(/([a-z])([A-Z])/g, "$1 $2"),
        value: value,
      }));
  }

  public static buildCrewMembers(users: User[]): CrewMember[] {
    return users.map((user) => ({
      id: user.id,
      employeeId: user.employeeId,
      accountIds: user.accountIds ?? [],
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.emailId,
      phone: user.phoneNumber,
      roleCode: user.roleCode,
      roleName: user.roleName,
      status: !user.active
        ? CrewStatus.Inactive
        : user.onLeave
          ? CrewStatus.OnLeave
          : CrewStatus.Active,
      onLeaveToday: user.onLeaveToday ?? false,
      canDrive: user.canDrive ?? false,
      skills: user.skills ?? [],
      baseRate: user.baseRate ?? null,
      overtimeRate: user.overtimeRate ?? null,
      availability: UserModel.buildCrewAvailability(user.availability),
    }));
  }

  public static buildCrewAvailability(
    availability: User["availability"],
  ): CrewAvailability {
    return CREW_DAYS.reduce(
      (result, day) => {
        const value = availability?.[day];

        return {
          ...result,
          [day]:
            value === true
              ? CREW_DEFAULT_DAY_HOURS
              : value && typeof value === "object"
                ? value
                : null,
        };
      },
      { ...CREW_EMPTY_AVAILABILITY },
    );
  }

  public static getCrewOvertimeRate(baseRate: number | null): number | null {
    if (baseRate === null || Number.isNaN(baseRate)) return null;

    return Math.round(baseRate * CREW_OVERTIME_MULTIPLIER * 100) / 100;
  }

  public static getCrewDisplayStatus(member: CrewMember): CrewStatus {
    if (member.status === CrewStatus.Inactive) return CrewStatus.Inactive;

    return member.status === CrewStatus.OnLeave || member.onLeaveToday
      ? CrewStatus.OnLeave
      : CrewStatus.Active;
  }

  public static buildCrewUserPayload(member: CrewMember): CrewUserPayload {
    return {
      onLeave: member.status === CrewStatus.OnLeave,
      canDrive: member.canDrive,
      baseRate: member.baseRate ?? undefined,
      overtimeRate: member.overtimeRate ?? undefined,
      skills: member.skills
        .filter((skill) => skill.id !== undefined)
        .map((skill) => ({
          id: skill.id as number,
          level: skill.level ?? CREW_DEFAULT_SKILL_LEVEL,
        })),
      availability: member.availability,
    };
  }

  public static buildCreateCrewUserPayload(member: CrewMember, orgId: number) {
    return {
      orgId,
      firstName: member.firstName,
      lastName: member.lastName,
      emailId: member.email,
      phoneNumber: member.phone,
      roleCode: member.roleCode,
      employeeId: "",
      active: true,
      ...UserModel.buildCrewUserPayload(member),
    };
  }

  public static buildUpdateCrewUserPayload(member: CrewMember, orgId: number) {
    return {
      id: member.id,
      orgId,
      firstName: member.firstName,
      lastName: member.lastName,
      emailId: member.email,
      phoneNumber: member.phone,
      roleCode: member.roleCode,
      employeeId: member.employeeId,
      accountIds: member.accountIds,
      active: member.status !== CrewStatus.Inactive,
      ...UserModel.buildCrewUserPayload(member),
    };
  }

  public static buildUser(users: User[]): UserInfo[] {
    return users.map((user) => ({
      ...user,
      fullName: `${user.firstName} ${user.lastName}`,
      employeeStatus: `${user.active ? "active" : "inactive"}`,
    }));
  }
}
